import { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, Quote } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getInitials(name) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

function Avatar({ avatarUrl, name }) {
  const [failed, setFailed] = useState(false);

  if (avatarUrl && !failed) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        onError={() => setFailed(true)}
        className="w-10 h-10 rounded-full object-cover shrink-0"
      />
    );
  }

  return (
    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center shrink-0">
      {getInitials(name)}
    </div>
  );
}

function RatingStars({ rating, size = 'w-4 h-4' }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          fill={star <= rating ? 'currentColor' : 'none'}
          className={`${size} ${star <= rating ? 'text-accent' : 'text-slate-200'}`}
        />
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      // Los testimonios manuales y las reseñas de Google se piden por separado:
      // si una de las dos fuentes falla, la otra igualmente se muestra.
      const manual = await axios.get(`${API}/testimonials`).catch((err) => {
        console.error('Error fetching testimonials:', err);
        return { data: [] };
      });

      const google = await axios.get(`${API}/google-reviews`).catch((err) => {
        console.error('Error fetching Google reviews:', err);
        return { data: { reviews: [] } };
      });

      const googleReviews = (google.data.reviews || []).map((r, index) => ({
        id: `google-${index}`,
        author_name: r.author_name,
        rating: r.rating,
        text: r.text,
        avatar_url: r.avatar_url,
      }));

      if (google.data.rating && google.data.user_rating_count) {
        setSummary({
          rating: google.data.rating,
          count: google.data.user_rating_count,
        });
      }

      setTestimonials([...manual.data, ...googleReviews]);
      setLoading(false);
    };
    fetchTestimonials();
  }, []);

  if (!loading && testimonials.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-secondary/10" data-testid="reviews-section">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h3 className="text-2xl font-serif font-semibold text-primary mb-2">
            ¿Qué dicen nuestros clientes?
          </h3>
          <p className="text-muted-foreground mb-3">
            Lee las opiniones de viajeros que ya han confiado en nosotros
          </p>
          {summary && (
            <div className="flex items-center justify-center gap-2" data-testid="rating-summary">
              <RatingStars rating={Math.round(summary.rating)} />
              <span className="font-semibold text-primary">{summary.rating.toFixed(1)}</span>
              <span className="text-muted-foreground text-sm">
                ({summary.count} opiniones en Google)
              </span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="skeleton h-48 rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6" data-testid="testimonials-grid">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="relative bg-white rounded-2xl shadow-lg p-8 border border-slate-100"
                data-testid={`testimonial-${testimonial.id}`}
              >
                <Quote className="absolute top-6 right-6 w-8 h-8 text-accent/20" />

                <RatingStars rating={testimonial.rating} />

                <p className="text-muted-foreground mb-6 mt-4 leading-relaxed">
                  "{testimonial.text}"
                </p>

                <div className="flex items-center gap-3">
                  <Avatar avatarUrl={testimonial.avatar_url} name={testimonial.author_name} />
                  <span className="font-semibold text-primary">{testimonial.author_name}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
